import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
interface MenuItem {
  id: string;
  label: string;
  href?: string;
  children?: MenuItem[];
}
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="27133" className="hidden lg:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-[#0F172A] transition-all hover:text-[#F89406] border-b-2 border-transparent hover:border-[#F89406] py-1 tracking-wider font-sans">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col p-6 gap-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-[#0F172A] py-2 border-b border-border/50 hover:text-[#F89406] transition-colors font-sans" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-4">
        <Button className="w-full bg-[#F89406] text-white hover:bg-[#F89406]/90 rounded-[3px]" asChild>
          <Link to="#quote" onClick={onClose}>
            Get a Quote
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b ${isScrolled ? "bg-white/80 backdrop-blur-md shadow-sm border-transparent" : "bg-white border-border"}`}>
      

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 lg:hidden">
          {}
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          
          {}
          <div className="absolute top-0 right-0 bottom-0 w-[80%] max-w-sm bg-white shadow-xl border-l border-border flex flex-col animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <span className="font-bold text-lg text-[#0F172A] font-sans">Menu</span>
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" className="text-[#0F172A]">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <MenuProvider menu_id="16">
                <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>

            <div className="p-6 border-t border-border bg-muted/30">
              <div className="flex items-center gap-3 text-[#343434] mb-2">
                <Phone className="h-4 w-4 text-[#F89406]" />
                <span className="text-sm font-medium font-sans">+45 70 20 10 30</span>
              </div>
            </div>
          </div>
        </div>}
    </header>;
}